import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "tip_entry_agreement";
const AGREEMENT_VERSION = 1;
const REACCEPT_DAYS = 30;

function hasValidAcceptance() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (data.version !== AGREEMENT_VERSION) return false;
    const daysSince = (Date.now() - new Date(data.acceptedAt).getTime()) / 86400000;
    return daysSince < REACCEPT_DAYS;
  } catch {
    return false;
  }
}

export default function EntryAgreementGate({ children, onAccepted }) {
  const [ready, setReady] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const alreadyAccepted = hasValidAcceptance();
    setShowModal(!alreadyAccepted);
    setReady(true);
    if (alreadyAccepted) onAccepted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnter = () => {
    if (!agreed) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: AGREEMENT_VERSION, acceptedAt: new Date().toISOString() }));
    setShowModal(false);
    onAccepted?.();
  };

  const handleLeave = () => {
    window.location.href = "https://www.google.com";
  };

  // Avoid a flash of unblurred content before we've checked storage
  if (!ready) return null;

  return (
    <>
      <div className={showModal ? "blur-xl pointer-events-none select-none brightness-95 transition-all duration-500" : "transition-all duration-500"} aria-hidden={showModal}>
        {children}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-sm" data-testid="entry-agreement-overlay" role="dialog" aria-modal="true">
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full p-7 sm:p-9 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-300" data-testid="entry-agreement-modal">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="relative">
                <FlaskConical className="h-6 w-6 text-brandblue-600 relative z-10" strokeWidth={1.5} />
                <div className="absolute inset-0 bg-brandblue-500 blur-md opacity-30 rounded-full" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-brandblue-600 font-semibold">True Impact Peptides</span>
            </div>

            <h2 className="text-2xl font-display font-semibold text-slate-900" data-testid="entry-agreement-title">Research Use Only Agreement</h2>

            <div className="mt-4 text-sm text-slate-600 leading-relaxed space-y-3">
              <p>Welcome to True Impact Peptides.</p>
              <p>Before entering this website, please acknowledge the following:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>I am 21 years of age or older.</li>
                <li>I understand that all products sold by True Impact Peptides are intended strictly for Research Use Only (RUO).</li>
                <li>I understand these products are not intended for human consumption.</li>
                <li>I acknowledge that these products are not intended to diagnose, treat, cure, or prevent any disease.</li>
                <li>I understand that I am responsible for ensuring my purchase and use of these products complies with all applicable federal, state, and local laws.</li>
              </ul>
              <p>By entering this website, I acknowledge and agree to abide by the policies and legal agreements governing this website.</p>
            </div>

            <div className="mt-6 flex items-start gap-3">
              <Checkbox id="entry-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="entry-agreement-checkbox" />
              <label htmlFor="entry-agree-checkbox" className="text-sm text-slate-700 font-medium leading-snug cursor-pointer">
                I have read and agree to the statements above.
              </label>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={handleLeave} data-testid="entry-agreement-leave-btn">
                Leave Website
              </Button>
              <Button type="button" className="flex-1 rounded-full btn-lift" disabled={!agreed} onClick={handleEnter} data-testid="entry-agreement-enter-btn">
                Enter Website
              </Button>
            </div>

            <p className="mt-4 text-xs text-slate-400 leading-relaxed">
              By selecting "Enter Website," you acknowledge that you have read and agree to our{" "}
              <Link to="/research-use-disclaimer" target="_blank" rel="noopener noreferrer" className="text-brandblue-600 hover:text-brandblue-700 underline" data-testid="entry-agreement-ruo-link">
                Research Use Only policies
              </Link>{" "}
              and website legal agreements.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
